import { getApp, getApps, initializeApp, type FirebaseOptions } from 'firebase/app';
import { browserLocalPersistence, connectAuthEmulator, getAuth, setPersistence, type Auth } from 'firebase/auth';

/**
 * Configuração client-side do Firebase (pública por natureza), por ambiente de build:
 * .env.production (produção), .env.hml (homologação), .env.development (local).
 * O navegador usa SOMENTE o Firebase Authentication; dados de negócio passam pela API.
 * Nunca incluir aqui chaves de service account, GEMINI_API_KEY ou outros segredos.
 */
function required(name: keyof ImportMetaEnv): string {
  const value = import.meta.env[name];
  // Sem fallback: um build sem a configuração do ambiente não pode cair em outro projeto.
  if (!value) throw new Error(`Configuração do Firebase ausente: ${name} (veja apps/web/.env.${import.meta.env.MODE}).`);
  return value;
}

export const firebaseConfig: FirebaseOptions = {
  apiKey: required('VITE_FIREBASE_API_KEY'),
  authDomain: required('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: required('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: required('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: required('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: required('VITE_FIREBASE_APP_ID'),
  ...(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID ? { measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID } : {}),
};

/** Ambiente da aplicação (production | hml | development). */
export const appEnv = import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE;

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const auth: Auth = getAuth(app);
auth.languageCode = 'pt-BR';

if (import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
  connectAuthEmulator(auth, import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL ?? 'http://127.0.0.1:9099', {
    disableWarnings: true,
  });
}

void setPersistence(auth, browserLocalPersistence);
